// Define the fixed order of settings based on backend categories
// This ensures settings are grouped and maintain fixed positions
export const categorySettingOrder = {
  intake: [
    // Localization & Language
    'localization',
    'languageOptions',

    // Page Content - All Languages
    'consentPageTextEnglish',
    'thankYouPageTextEnglish',
    'disagreePageTextEnglish',
    'privacyPolicyTextEnglish',
    'consentPageTextArabic',
    'thankYouPageTextArabic',
    'disagreePageTextArabic',
    'privacyPolicyTextArabic',
    'consentPageTextFrenchCanadian',
    'thankYouPageTextFrenchCanadian',
    'disagreePageTextFrenchCanadian',
    'privacyPolicyTextFrenchCanadian',
    'consentPageTextSpanishUS',
    'thankYouPageTextSpanishUS',
    'disagreePageTextSpanishUS',
    'privacyPolicyTextSpanishUS',
    'consentPageTextChineseSimplified',
    'thankYouPageTextChineseSimplified',
    'disagreePageTextChineseSimplified',
    'privacyPolicyTextChineseSimplified',
    'consentPageTextChineseTraditional',
    'thankYouPageTextChineseTraditional',
    'disagreePageTextChineseTraditional',
    'privacyPolicyTextChineseTraditional',

    // Logos
    'consentPageLogoOn',
    'consentPageLogo',
    'thankYouPageLogo',
    'disagreePageLogo',
    'qrCodePageLogo',
    'logoWidthPercent',

    // Theme Colors
    'primaryTheme',
    'activeButtonThemeColor',
    'inactiveButtonThemeColor',
    'activeButtonHoverThemeColor',
    'unselectedButtonThemeColor',
    'customTheme',
    'fontTheme',

    // Intake Behavior
    'qrUseAgeInsteadOfDob',
    'disagreeButtonDisabled',
    'privacyPolicyEnabled',
    'reportHeader',
    'historyHeader',
    'excludeChiefComplaintInHistory',
    'useMedicalNameWhenReportingSymptoms',
    'useImperialUnitsWhenReportingHeightAndWeight',
    'includeIcdCode',
    'icdPrompt',
    'includeMinPractitionerScope',
    'translatePatientFreeTextToEnglish',
    'medicationFormType',
    'enableFeedbackText',
    'preventEmptyFreetextResponses',
    'patientCanDownloadReportOnFeedbackQuestion',
    'enableLongitudinalHistory',

    // SMS Delivery Configuration
    'smsProvider',
    'smsFromPhoneNumber',
    'patientSupportEmailRecipient',

    // Timing
    'linkExpiryTime',
    'idleThresholdMinutes',
    'expiryHourOnAppointmentDate',
  ],
  medical: [
    'intakePlans',
    'qrCodeIntakePlan',
    'skipCaptcha',
    'qrCodeActiveStartHour',
    'qrCodeActiveEndHour',
    'qrCodeActiveDays',
    'maxSymptomsToExplore',
  ],
  api: [
    // Accuro Integration
    'accuroEnabled',
    'accuroUUID',
    'accuroClientId',
    'accuroClientSecret',
    'accuroAccessToken',
    'accuroRefreshToken',
    'accuroAccessTokenExpireTime',
    'accuroRefreshTokenExpireTime',
    'accuroPreBookedAutomationEnabled',
    'accuroSameDayAutomationEnabled',
    'accuroFilterByProviderIds',
    'accuroProviderIds',
    'accuroAppointmentReasonIds',
    'accuroAppointmentType',
    'accuroSentSMSStatus',
    'accuroNotSentSMSStatus',
    'accuroIntakeCompletedStatus',
    'accuroSendDays',
    'accuroDaysBeforeAppointment',
    'accuroFolderId',
    'accuroSubfolderId',

    // Athena Integration
    'athenaEnabled',
    'athenaPracticeId',
    'athenaDepartmentId',
    'athenaProviders',

    // PCC Integration
    'pccEnabled',
    'pccOrgId',
    'pccOrgUUID',
    'pccFacId',

    // AdvancedMD Integration
    'advancedMDEnabled',
    'advancedMDOfficeCode',
    'advancedMDUsername',
    'advancedMDPassword',
    'advancedMDDaysBeforeAppointment',
    'advancedMDAppointmentTypes',

    // ECW Integration
    'ecwEnabled',
    'ecwDaysBeforeAppointment',
    'ecwAppointmentTypes',
    'ecwSiuFolderPath',
    'ecwPnFolderPath',
    'ecwReceivingFacility',

    // Telus Integration
    'telusEnabled',
    'telusAuthCredentials',
    'telusProviderIds',
    'telusIntakePlanId',
    'telusAppointmentCutoffDateTime',
    'telusOperatingHours',
    'telusTimezone',
    'telusSmsHoursBeforeAppointment',

    // Canvas Integration
    'canvasEnabled',
    'canvasClientId',
    'canvasClientSecret',
    'canvasDaysBeforeAppointment',
    'canvasPractitionerIds',

    // Notifications
    'notificationEnabled',
    'notificationSlackWebHookUrl',
    'notificationSlackWebHookMessage',

    // Push to Endpoint
    'pushToEndpointEnabled',
    'pushToEndpointIntegrationType',
    'pushToEndpointUrl',
    'pushToEndpointToken',
    'pushToEndpointApiKey',
    'pushToEndpointApiSecret',

    // SFTP
    'sftpAutomationEnabled',
    'sftpHost',
    'sftpPort',
    'sftpUsername',
    'sftpPassword',
    'sftpRemoteDirectory',

    // Other API Settings
    'integrationEmail',
    'rpaDaysBeforeAppointment',
    'keepPiiAfterEmrPush',
  ],
  hxManager: [
    // General subcategory
    'canEditReport',
    'requireUniqueId',
    'sendsTestDrives',
    // SMS Configuration subcategory
    'smsMessage',
    'numberOfTimesToResendSms',
    // LLM / AI subcategory
    'llmSummaryEnabled',
    'llmSummaryPrompt',
    'llmOutputLocation',
    'generateDDx',
    'DDxPrompt',
    // Medscheck subcategory
    'generateMedsCheckPmrPdf',
    'pharmacistName',
    'pharmacyName',
    'pharmacyUnitNumber',
    'pharmacyStreetNumber',
    'pharmacyStreetName',
    'pharmacyPoBox',
    'pharmacyCity',
    'pharmacyProvince',
    'pharmacyPostalCode',
    'pharmacyPhone',
    'pharmacyFax',
    // Scribe subcategory
    'scribeEnabled',
    'scribeNoteVerbosity',
    'scribeNoteTemplate',
  ],
  geography: [],
};

export function getCategorySettingOrder(categoryId) {
  return categorySettingOrder[categoryId] || [];
}

export const intakeSubcategoryMapping = {
  localization: ['localization', 'languageOptions'],
  pageContent: [
    'consentPageTextEnglish',
    'thankYouPageTextEnglish',
    'disagreePageTextEnglish',
    'privacyPolicyTextEnglish',
    'consentPageTextArabic',
    'thankYouPageTextArabic',
    'disagreePageTextArabic',
    'privacyPolicyTextArabic',
    'consentPageTextFrenchCanadian',
    'thankYouPageTextFrenchCanadian',
    'disagreePageTextFrenchCanadian',
    'privacyPolicyTextFrenchCanadian',
    'consentPageTextSpanishUS',
    'thankYouPageTextSpanishUS',
    'disagreePageTextSpanishUS',
    'privacyPolicyTextSpanishUS',
    'consentPageTextChineseSimplified',
    'thankYouPageTextChineseSimplified',
    'disagreePageTextChineseSimplified',
    'privacyPolicyTextChineseSimplified',
    'consentPageTextChineseTraditional',
    'thankYouPageTextChineseTraditional',
    'disagreePageTextChineseTraditional',
    'privacyPolicyTextChineseTraditional',
  ],
  logos: [
    'consentPageLogoOn',
    'consentPageLogo',
    'thankYouPageLogo',
    'disagreePageLogo',
    'qrCodePageLogo',
    'logoWidthPercent',
  ],
  theme: [
    'primaryTheme',
    'activeButtonThemeColor',
    'inactiveButtonThemeColor',
    'activeButtonHoverThemeColor',
    'unselectedButtonThemeColor',
    'customTheme',
    'fontTheme',
  ],
  behavior: [
    'qrUseAgeInsteadOfDob',
    'disagreeButtonDisabled',
    'privacyPolicyEnabled',
    'reportHeader',
    'historyHeader',
    'excludeChiefComplaintInHistory',
    'useMedicalNameWhenReportingSymptoms',
    'useImperialUnitsWhenReportingHeightAndWeight',
    'includeIcdCode',
    'icdPrompt',
    'includeMinPractitionerScope',
    'translatePatientFreeTextToEnglish',
    'medicationFormType',
    'enableFeedbackText',
    'preventEmptyFreetextResponses',
    'patientCanDownloadReportOnFeedbackQuestion',
  ],
  message: [
    'smsProvider',
    'smsFromPhoneNumber',
    'patientSupportEmailRecipient',
  ],
  timing: [
    'linkExpiryTime',
    'idleThresholdMinutes',
    'expiryHourOnAppointmentDate',
  ],
};

export function getSettingsForIntakeSubcategory(subcategoryId) {
  return intakeSubcategoryMapping[subcategoryId] || [];
}

export const hxManagerSubcategoryMapping = {
  general: [
    'canEditReport',
    'requireUniqueId',
    'sendsTestDrives',
  ],
  sms: [
    'smsMessage',
    'numberOfTimesToResendSms',
  ],
  llm: [
    'llmSummaryEnabled',
    'llmSummaryPrompt',
    'llmOutputLocation',
    'generateDDx',
    'DDxPrompt',
  ],
  medscheck: [
    'generateMedsCheckPmrPdf',
    'pharmacistName',
    'pharmacyName',
    'pharmacyUnitNumber',
    'pharmacyStreetNumber',
    'pharmacyStreetName',
    'pharmacyPoBox',
    'pharmacyCity',
    'pharmacyProvince',
    'pharmacyPostalCode',
    'pharmacyPhone',
    'pharmacyFax',
  ],
  scribe: [
    'scribeEnabled',
    'scribeNoteVerbosity',
    'scribeNoteTemplate',
  ],
};

export function getSettingsForHxManagerSubcategory(subcategoryId) {
  return hxManagerSubcategoryMapping[subcategoryId] || [];
}

export function sortSettingNamesByOrder(settingNames, positionMap) {
  return settingNames.sort((a, b) => {
    const posA = positionMap[a] !== undefined ? positionMap[a] : 99999;
    const posB = positionMap[b] !== undefined ? positionMap[b] : 99999;

    if (posA !== posB) {
      return posA - posB;
    }
    // If both are not in the predefined order, sort alphabetically
    return a.localeCompare(b);
  });
}
